import type { YourInformationFormState } from './RequestYourInformation';
import { dateOnlyFormat } from '../../util/dateUtil';
import { AWARD_PAYMENT } from '../../constants/general';
import { normalizePaymentRequestStatus } from './paymentRequestStatuses';
import { parseDateTime } from './awardUtils';

type SeniorProfile = {
  first_name?: string;
  last_name?: string;
  gpa?: number | string | null;
  admission_date?: string | null;
  graduation_date?: string | null;
  userId?: number | null;
  alumni_updated_first_name?: string | null;
  alumni_updated_last_name?: string | null;
};

type AwardPaymentRequestForPrefill = {
  status?: string | null;
  submittedAt?: string | null;
  submittedAtTimestamp?: number | null;
  schoolInfo?: {
    dateOfEnrollment?: string | null;
    plannedGraduationDate?: string | null;
  } | null;
};

const toDateInputValue = (value?: string | null) => {
  if (!value) return '';
  return dateOnlyFormat(value) ?? '';
};

export const getMostRecentUnderReviewSchoolInfo = (requests?: AwardPaymentRequestForPrefill[] | null) => {
  const underReviewRequests = (requests ?? []).filter((request) => {
    const status = request?.status ?? '';
    return normalizePaymentRequestStatus(status) === AWARD_PAYMENT.UNDER_REVIEW;
  });

  if (!underReviewRequests.length) {
    return {
      dateOfEnrollment: '',
      plannedGraduationDate: '',
    };
  }

  const latestRequest = [...underReviewRequests].sort((left, right) => {
    const leftTimestamp = typeof left?.submittedAtTimestamp === 'number' && Number.isFinite(left.submittedAtTimestamp)
      ? left.submittedAtTimestamp
      : left?.submittedAt
        ? parseDateTime(left.submittedAt)?.toMillis() ?? 0
        : 0;
    const rightTimestamp = typeof right?.submittedAtTimestamp === 'number' && Number.isFinite(right.submittedAtTimestamp)
      ? right.submittedAtTimestamp
      : right?.submittedAt
        ? parseDateTime(right.submittedAt)?.toMillis() ?? 0
        : 0;
    return rightTimestamp - leftTimestamp;
  })[0];

  const schoolInfo = latestRequest?.schoolInfo ?? {};

  return {
    dateOfEnrollment: toDateInputValue(schoolInfo.dateOfEnrollment),
    plannedGraduationDate: toDateInputValue(schoolInfo.plannedGraduationDate),
  };
};

export const mapSeniorToAwardPaymentYourInformationState = (senior?: SeniorProfile | null): YourInformationFormState | null => {
  if (
    !senior ||
    (!(senior.first_name || senior.alumni_updated_first_name) && !(senior.last_name || senior.alumni_updated_last_name) && !senior.userId)
  ) {
    return null;
  }

  return {
    userId: senior.userId ?? 0,
    firstName: senior.alumni_updated_first_name ?? senior.first_name ?? '',
    lastName: senior.alumni_updated_last_name ?? senior.last_name ?? '',
    enrolledInSchool: 'yes',
    schoolInfo: {
      currentMajor: '',
      dateOfEnrollment: '',
      plannedGraduationDate: '',
      gpa: senior.gpa !== null && senior.gpa !== undefined ? String(senior.gpa) : '',
    },
    employmentStatus: undefined,
    employmentInfo: {
      placeOfEmployment: '',
      industryTrade: '',
      startDate: '',
      paySchedule: '',
      averageHoursWorkedPerWeek: '',
      positionTitle: '',
    },
  };
};
